import classNames from "classnames";
import React from "react";
import useMountEntrance, { IMountEntrance } from "../../../shared/hooks/useMountEntrance";
import KuikaH5 from "../typography/H5/H5";
import KuikaText from "../typography/Text/Text";
import "./kuika-create-hero.scss";

/**
 * The centred "this tool, and how to start one" panel: an illustration, the tool's name, a
 * line about what it is for, and — optionally — the form or button that starts one.
 *
 * It was a global `.kuika-dashboard__create` class that eight surfaces reached for, styling
 * bare `.title` / `.description` spans nested inside it. Tool landing pages that list what
 * you already own have moved to `KuikaListPage`; this is what the remaining surfaces are —
 * a single-purpose create screen, or an empty state with somewhere to go.
 */

export interface KuikaCreateHeroProps {
  /** The hero artwork, typically an imported `svgs/create-hero-svgs/*.svg`. */
  illustration: React.ReactNode;
  /** The tool's name. */
  title: string;
  /** One or two lines on what the tool is for. */
  description?: React.ReactNode;
  /** The create form or button. Omitted by surfaces that only explain (Zendesk, the wizard). */
  children?: React.ReactNode;
  /**
   * Sizes the hero to its content instead of filling its host. For hosts that stack it with
   * other content rather than giving it the whole area.
   */
  fitContent?: boolean;
  titleTestId?: string;
  descriptionTestId?: string;
  className?: string;
  "data-testid"?: string;
}

const KuikaCreateHero = ({
  illustration,
  title,
  description,
  children,
  fitContent = false,
  titleTestId,
  descriptionTestId,
  className,
  "data-testid": dataTestId
}: KuikaCreateHeroProps): JSX.Element => {
  // Illustration → title → description → body, staggered once on mount; the entrance ends on the
  // last of them the hero renders.
  const { isEntering, onAnimationEnd }: IMountEntrance = useMountEntrance();
  const hasBody: boolean = Boolean(children);
  const hasDescription: boolean = Boolean(description);
  return (
    <div
      className={classNames(
        "kuika-create-hero",
        { "kuika-create-hero--fit-content": fitContent, "kuika-create-hero--entering": isEntering },
        className
      )}
      data-testid={dataTestId}
      onAnimationEnd={isEntering ? onAnimationEnd : undefined}
    >
      <span className="kuika-create-hero__illustration">{illustration}</span>
      <KuikaH5
        className="kuika-create-hero__title"
        data-testid={titleTestId}
        data-entrance-last={(!hasDescription && !hasBody) || undefined}
      >
        {title}
      </KuikaH5>
      {hasDescription && (
        <KuikaText
          className="kuika-create-hero__description"
          data-testid={descriptionTestId}
          data-entrance-last={!hasBody || undefined}
        >
          {description}
        </KuikaText>
      )}
      {hasBody && (
        <div className="kuika-create-hero__body" data-entrance-last>
          {children}
        </div>
      )}
    </div>
  );
};

export default KuikaCreateHero;
